(() => {
  'use strict';
  const keys = "qwertyuiop[]asdfghjkl;'zxcvbnm,.";
  const ukrainian = 'йцукенгшщзхїфівапролджєячсмитьбю';
  const layout = Object.fromEntries([...keys].map((key, index) => [key, ukrainian[index]]));
  const instances = new Map();
  const originalFlag = 'bc_layout_original';

  function correctedLayout(query) {
    if (/[а-яіїєґ]/iu.test(query)) return null;
    let letters = 0;
    const corrected = [...query].map(character => {
      const lower = character.toLowerCase();
      const replacement = layout[lower];
      if (!replacement) return character;
      if (/[a-z]/i.test(character)) letters += 1;
      return character === lower ? replacement : replacement.toUpperCase();
    }).join('');
    return letters >= 3 && corrected !== query ? corrected : null;
  }

  function normalizeQuery(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('uk-UA');
  }

  function hasStrongMatch(query, title, author, isbn = '') {
    const needle = normalizeQuery(query);
    if (!needle) return false;
    return [title, author, ...String(isbn || '').split('|')]
      .some(value => normalizeQuery(value).includes(needle));
  }

  function strongProducts(container, query, includeIsbn = false) {
    if (!container) return [];
    return [...container.querySelectorAll('.search-flydown--product')].filter(product => hasStrongMatch(
      query,
      product.dataset.bcSearchTitle || product.querySelector('.search-flydown--product-title')?.textContent,
      product.dataset.bcSearchAuthor || product.querySelector('.bc-search-author')?.textContent,
      includeIsbn ? product.dataset.bcSearchIsbn : '',
    ));
  }

  function correctionCandidate(query, strongOriginalCount, optOut = false) {
    return strongOriginalCount === 0 && !optOut ? correctedLayout(query) : null;
  }

  function splitQuery(query) {
    const separator = query.indexOf(' AND ');
    if (/^(tag|product_type):/.test(query) && separator >= 0) {
      return { prefix: query.slice(0, separator + 5), typed: query.slice(separator + 5) };
    }
    return { prefix: '', typed: query };
  }

  function searchUrl(query, original = null, optOut = false) {
    const url = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    if (original) url.searchParams.set('bc_layout_from', original);
    if (optOut) url.searchParams.set(originalFlag, '1');
    return url;
  }

  async function predictiveProducts(query, signal) {
    const url = new URL(window.Theme?.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
    const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const products = fragment.querySelector('.search-flydown--product-items');
    const typed = splitQuery(query).typed;
    return {
      count: products?.querySelectorAll('.search-flydown--product').length || 0,
      strongCount: strongProducts(products, typed).length,
      originalStrongCount: strongProducts(products, typed, true).length,
      items: products,
    };
  }

  function makeLink(query, original, label, optOut = false) {
    const link = document.createElement('a');
    link.className = 'bc-layout-correction__link';
    link.href = searchUrl(query, original, optOut).toString();
    link.textContent = label;
    return link;
  }

  function enhanceLiveSearch(form) {
    if (instances.has(form)) return;
    const input = form.querySelector('[data-live-search-input]');
    const flydown = form.querySelector('[data-live-search-flydown]');
    const results = form.querySelector('[data-live-search-results]');
    const filter = form.querySelector('[data-live-search-filter]');
    if (!input || !flydown || !results) return;
    let current = null;
    let controller;
    let attempted = null;
    let generation = 0;
    let keyupTimer;
    const pageUrl = new URL(window.location.href);
    let optOutQuery = pageUrl.searchParams.get(originalFlag) === '1'
      ? splitQuery(pageUrl.searchParams.get('q') || '').typed.trim() : null;

    function context() {
      const original = input.value.trim();
      const prefix = filter?.value ? `${filter.value} AND ` : '';
      return { original, prefix, request: prefix + original };
    }

    function clearCorrection() {
      controller?.abort();
      generation += 1;
      current = null;
      attempted = null;
      results.classList.remove('bc-layout-corrected');
      results.querySelector('.bc-layout-correction')?.remove();
    }

    function resizeFlydown() {
      if (flydown.dataset.animationState !== 'open') return;
      const visible = [...flydown.children].find(node => node.classList.contains('visible'));
      if (visible) flydown.style.setProperty('--open-height', `${visible.scrollHeight}px`);
    }

    function panelWithMessage(message, loading = false) {
      results.querySelector('.bc-layout-correction')?.remove();
      const panel = document.createElement('div');
      panel.className = `bc-layout-correction${loading ? ' bc-layout-correction--loading' : ''}`;
      const title = document.createElement('p');
      title.className = 'bc-layout-correction__title';
      title.setAttribute('role', 'status');
      title.textContent = message;
      panel.append(title);
      results.append(panel);
      return panel;
    }

    async function considerOriginalResults() {
      // Read the native response instead of making a duplicate original-query request.
      const metadata = results.querySelector('[data-bc-search-query]');
      if (!metadata) return;
      const now = context();
      if (normalizeQuery(metadata.dataset.bcSearchQuery) !== normalizeQuery(now.request)) return;
      // Shopify may return loose fuzzy matches (for example "vffc" -> "Блок D").
      // Preserve literal matches in the actual title, author or variant barcode.
      const strongOriginalCount = strongProducts(metadata, now.original, true).length;
      const corrected = correctionCandidate(now.original, strongOriginalCount, now.original === optOutQuery);
      if (!corrected) {
        if (current || results.querySelector('.bc-layout-correction')) clearCorrection();
        return;
      }
      if (attempted === now.request) {
        // Empire can replace the native response for the same trimmed query.
        // Reuse its completed correction instead of fetching it again.
        if (current?.request === now.request && current.panel.parentNode !== results) {
          results.append(current.panel);
          results.classList.add('bc-layout-corrected');
          resizeFlydown();
        }
        return;
      }
      attempted = now.request;
      controller?.abort();
      controller = new AbortController();
      const requestGeneration = ++generation;
      panelWithMessage('Перевіряємо розкладку клавіатури…', true);
      resizeFlydown();
      try {
        const result = await predictiveProducts(now.prefix + corrected, controller.signal);
        if (requestGeneration !== generation || context().request !== now.request) return;
        if (!result.strongCount || !result.items) {
          results.querySelector('.bc-layout-correction')?.remove();
          resizeFlydown();
          return;
        }
        const panel = panelWithMessage(`Результати для «${corrected}»`);
        current = { ...now, corrected, panel };
        const correctedItems = result.items.cloneNode(true);
        const strongItems = new Set(strongProducts(correctedItems, corrected));
        correctedItems.querySelectorAll('.search-flydown--product').forEach(product => {
          if (!strongItems.has(product)) (product.closest('li') || product).remove();
        });
        panel.append(correctedItems);
        panel.append(makeLink(now.prefix + corrected, now.original, `Усі результати для «${corrected}»`));
        panel.append(makeLink(now.request, null, `Шукати «${now.original}» без виправлення`, true));
        results.classList.add('bc-layout-corrected');
        resizeFlydown();
      } catch (error) {
        if (requestGeneration !== generation) return;
        results.querySelector('.bc-layout-correction')?.remove();
        results.classList.remove('bc-layout-corrected');
        resizeFlydown();
        // Native Shopify results and submission remain usable on network errors.
      }
    }

    const observer = new MutationObserver(considerOriginalResults);
    observer.observe(results, { childList: true });
    // A fast fallback can finish during Empire's opening animation; resize again once it settles.
    const animationObserver = new MutationObserver(resizeFlydown);
    animationObserver.observe(flydown, { attributes: true, attributeFilter: ['data-animation-state'] });
    const onInput = event => {
      // A trailing space leaves the request unchanged; keep its result or in-flight work.
      if (context().request !== attempted) clearCorrection();
      if (input.value.trim() !== optOutQuery) optOutQuery = null;
      clearTimeout(keyupTimer);
      // Empire listens for keyup; this also covers paste/clear without changing its search implementation.
      if (!event.isComposing) keyupTimer = window.setTimeout(() => {
        input.dispatchEvent(new Event('keyup', { bubbles: true }));
        // A reused native response may not mutate the DOM. Its query guard rejects stale data.
        considerOriginalResults();
      }, 0);
    };
    const onFilter = () => { clearCorrection(); };
    const onSubmit = event => {
      const now = context();
      const shouldUseCorrection = current && current.request === now.request;
      const shouldKeepOriginal = now.original === optOutQuery;
      if (!shouldUseCorrection && !shouldKeepOriginal) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(shouldUseCorrection
        ? searchUrl(now.prefix + current.corrected, now.original).toString()
        : searchUrl(now.request, null, true).toString());
    };
    input.addEventListener('input', onInput);
    filter?.addEventListener('change', onFilter);
    form.addEventListener('submit', onSubmit, true);
    instances.set(form, () => {
      controller?.abort();
      clearTimeout(keyupTimer);
      observer.disconnect();
      animationObserver.disconnect();
      input.removeEventListener('input', onInput);
      filter?.removeEventListener('change', onFilter);
      form.removeEventListener('submit', onSubmit, true);
    });
  }

  async function enhanceSearchPage() {
    const masthead = document.querySelector('.productgrid--masthead');
    if (!masthead) return;
    const url = new URL(window.location.href);
    const query = url.searchParams.get('q')?.trim();
    if (!query || url.searchParams.get(originalFlag) === '1') return;
    const { prefix, typed } = splitQuery(query);
    const original = url.searchParams.get('bc_layout_from');
    if (original) {
      const notice = document.createElement('div');
      notice.className = 'bc-layout-page-notice';
      const text = document.createElement('span');
      text.textContent = `Показуємо результати для «${typed}» замість «${original}». `;
      notice.append(text, makeLink(prefix + original, null, 'Шукати за початковим запитом', true));
      masthead.append(notice);
      document.querySelectorAll('[data-live-search-input]').forEach(input => { input.value = original; });
      return;
    }
    // Do not reinterpret a valid query merely because selected filters removed its results.
    if ([...url.searchParams.keys()].some(key => key.startsWith('filter.'))) return;
    const cards = [...document.querySelectorAll('.productgrid--items [data-product-item]')];
    const strongOnPage = cards.some(card => hasStrongMatch(
      typed,
      card.querySelector('.productitem--title')?.textContent,
      card.querySelector('.productitem--author')?.textContent,
    ));
    const corrected = correctionCandidate(typed, strongOnPage ? 1 : 0);
    if (!corrected) return;
    try {
      // The full grid does not expose barcodes: protect any strong original ISBN/title match
      // in its predictive response before replacing a page containing loose matches.
      if (!document.querySelector('.productgrid--no-results')) {
        const originalResult = await predictiveProducts(query);
        if (originalResult.originalStrongCount > 0) return;
      }
      const result = await predictiveProducts(prefix + corrected);
      if (result.strongCount > 0) window.location.replace(searchUrl(prefix + corrected, typed).toString());
    } catch (_) {
      // Preserve Shopify's original empty state if the optional fallback is unavailable.
    }
  }

  function start(scope = document) {
    scope.querySelectorAll('[data-live-search-form]').forEach(enhanceLiveSearch);
  }
  function ready() { start(); enhanceSearchPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, form) => { if (event.target.contains(form)) { destroy(); instances.delete(form); } });
  });
})();
